import { z } from 'zod';

import { FINANCIAL_PRODUCT } from '../constants';

type FinancialProductShape = { [K in keyof typeof FINANCIAL_PRODUCT]?: z.ZodRawShape };

type CustomSchemaType<T extends FinancialProductShape> = {
  [K in keyof typeof FINANCIAL_PRODUCT]?: T[K];
};

type GenerateSchemaType<T extends FinancialProductShape, Z extends z.ZodRawShape> = {
  customSchemas: CustomSchemaType<T>;
  defaultSchema: Z;
};

type SchemaWithFinancialProductId<T extends z.ZodRawShape> = T & {
  financialProductId: z.ZodLiteral<keyof typeof FINANCIAL_PRODUCT>;
};

/**
 * Generate schema based on custom or default schema passed to it.
 *
 */

export const generateSchema = <T extends FinancialProductShape, Z extends z.ZodRawShape>({
  customSchemas,
  defaultSchema,
}: GenerateSchemaType<T, Z>) => {
  const customProductIds = Object.keys(customSchemas) as (keyof typeof FINANCIAL_PRODUCT)[];

  const defaultProductsToUse = getDefaultProductsToUse(customProductIds);
  const defaultSchemasToUse = generateDefaultSchemas(defaultSchema, defaultProductsToUse);
  const customSchemasToUse = generateCustomSchemas(customSchemas, customProductIds);

  return z.discriminatedUnion('financialProductId', [
    //discriminatedUnion needs one schema as default
    defaultSchemasToUse[0],
    ...customSchemasToUse,
    ...defaultSchemasToUse.slice(1),
  ]);
};

const getDefaultProductsToUse = (customProductIds: (keyof typeof FINANCIAL_PRODUCT)[]) => {
  return (Object.values(FINANCIAL_PRODUCT) as (keyof typeof FINANCIAL_PRODUCT)[]).filter(
    (product: keyof typeof FINANCIAL_PRODUCT) => !customProductIds.includes(product)
  );
};

const generateDefaultSchemas = <T extends z.ZodRawShape>(
  defaultSchema: T,
  defaultProductsToUse: (keyof typeof FINANCIAL_PRODUCT)[]
) => {
  return defaultProductsToUse.flatMap((product) =>
    z.object(defaultSchema).merge(
      z.object({
        financialProductId: z.literal(product),
      })
    )
  ) as z.ZodObject<SchemaWithFinancialProductId<T>>[];
};

const generateCustomSchemas = <T extends FinancialProductShape>(
  customSchemas: CustomSchemaType<T>,
  customProductIds: (keyof typeof FINANCIAL_PRODUCT)[]
) => {
  return customProductIds.flatMap((product) => {
    const schema = customSchemas[product];
    return ((schema &&
      z.object(schema)?.merge(
        z.object({
          financialProductId: z.literal(product),
        })
      )) ??
      []) as z.ZodObject<SchemaWithFinancialProductId<z.ZodRawShape>>[];
  });
};
